import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { messagesAPI } from '../api';
import { useAuth } from '../hooks/useAuth';
import { useSocket } from '../hooks/useSocket';
import LoadingSpinner from '../components/LoadingSpinner';

const MessagesPage = () => {
  const { user } = useAuth();
  const { conversationId } = useParams();
  const { sendMessage, onNewMessage, emitTyping, emitStopTyping, onTyping, onUserOnline, onUserOffline } = useSocket();
  const [conversations, setConversations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [activeConv, setActiveConv] = useState(conversationId || null);
  const [newMsg, setNewMsg] = useState('');
  const [loading, setLoading] = useState(true);
  const [otherTyping, setOtherTyping] = useState(false);
  const [onlineUsers, setOnlineUsers] = useState(new Set());
  const messagesEndRef = useRef(null);
  const typingTimeout = useRef(null);
  const typingIndicatorTimeout = useRef(null);

  useEffect(() => {
    messagesAPI.getConversations()
      .then((res) => setConversations(res.data.conversations || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!activeConv) return;
    messagesAPI.getMessages(activeConv)
      .then((res) => setMessages(res.data.messages || []))
      .catch(console.error);
  }, [activeConv]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    const unsub = onNewMessage((msg) => {
      if (String(msg.conversation_id) === String(activeConv)) {
        setMessages((prev) => [...prev, msg]);
      }
      setConversations((prev) => {
        const exists = prev.find((c) => String(c.id) === String(msg.conversation_id));
        if (exists) {
          return prev.map((c) =>
            String(c.id) === String(msg.conversation_id)
              ? { ...c, last_message: msg.message, last_message_at: msg.created_at, unread_count: String(c.id) === String(activeConv) ? 0 : (parseInt(c.unread_count) || 0) + 1 }
              : c
          );
        }
        // New conversation — refresh the list
        messagesAPI.getConversations().then((res) => setConversations(res.data.conversations || [])).catch(console.error);
        return prev;
      });
    });
    return unsub;
  }, [activeConv, onNewMessage]);

  // Typing indicator
  useEffect(() => {
    if (!activeConv) return;
    const unsub = onTyping((data) => {
      if (String(data.conversation_id) === String(activeConv)) {
        setOtherTyping(true);
        clearTimeout(typingIndicatorTimeout.current);
        typingIndicatorTimeout.current = setTimeout(() => setOtherTyping(false), 3000);
      }
    });
    return unsub;
  }, [activeConv, onTyping]);

  // Online/offline tracking
  useEffect(() => {
    const unsubOn = onUserOnline((data) => setOnlineUsers(prev => new Set(prev).add(String(data.userId))));
    const unsubOff = onUserOffline((data) => setOnlineUsers(prev => { const s = new Set(prev); s.delete(String(data.userId)); return s; }));
    return () => { unsubOn(); unsubOff(); };
  }, [onUserOnline, onUserOffline]);

  const handleSend = (e) => {
    e.preventDefault();
    if (!newMsg.trim() || !activeConv) return;
    const conv = conversations.find((c) => String(c.id) === String(activeConv));
    const receiverId = conv ? conv.other_user_id : null;
    if (!receiverId) return;
    sendMessage(receiverId, newMsg.trim());
    setMessages((prev) => [
      ...prev,
      { sender_id: user.id, message: newMsg.trim(), created_at: new Date().toISOString() },
    ]);
    setNewMsg('');
  };

  const handleTyping = () => {
    if (!activeConv) return;
    const conv = conversations.find((c) => String(c.id) === String(activeConv));
    const receiverId = conv ? conv.other_user_id : null;
    if (receiverId) emitTyping(receiverId, activeConv);
    clearTimeout(typingTimeout.current);
    typingTimeout.current = setTimeout(() => { if (receiverId) emitStopTyping(receiverId, activeConv); }, 2000);
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="min-h-screen bg-gray-50/50">
      <div className="max-w-6xl mx-auto px-4 py-8">
        <h1 className="text-2xl font-bold mb-6">Messages</h1>
        <div className="flex bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden" style={{ height: '70vh' }}>
          {/* Conversation List */}
          <div className={`w-full md:w-1/3 border-r border-gray-100 overflow-y-auto ${activeConv ? 'hidden md:block' : ''}`}>
            {conversations.length === 0 ? (
              <div className="p-8 text-center text-gray-500">No conversations yet</div>
            ) : (
              conversations.map((conv) => {
                const isOnline = onlineUsers.has(String(conv.other_user_id));
                const unread = parseInt(conv.unread_count) || 0;
                return (
                  <button key={conv.id} onClick={() => setActiveConv(conv.id)}
                    className={`w-full text-left p-4 border-b border-gray-100 hover:bg-gray-50 flex items-center gap-3 ${String(activeConv) === String(conv.id) ? 'bg-primary-50' : ''}`}>
                    <div className="relative flex-shrink-0">
                      <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center text-primary-700 font-semibold">
                        {conv.other_first_name?.[0]}{conv.other_last_name?.[0]}
                      </div>
                      <span className={`absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full border-2 border-white ${isOnline ? 'bg-green-400' : 'bg-gray-300'}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <p className={`text-sm truncate ${unread > 0 ? 'font-bold' : 'font-medium'}`}>{conv.other_first_name} {conv.other_last_name}</p>
                        {unread > 0 && (
                          <span className="bg-primary-600 text-white text-[10px] font-bold min-w-[18px] h-[18px] rounded-full flex items-center justify-center px-1 flex-shrink-0 ml-2">{unread}</span>
                        )}
                      </div>
                      <p className={`text-xs truncate ${unread > 0 ? 'text-gray-700 font-medium' : 'text-gray-500'}`}>{conv.last_message || 'Start a conversation'}</p>
                    </div>
                  </button>
                );
              })
            )}
          </div>

          {/* Messages Area */}
          <div className={`flex-1 flex flex-col ${!activeConv ? 'hidden md:flex' : ''}`}>
            {!activeConv ? (
              <div className="flex-1 flex items-center justify-center text-gray-400">Select a conversation</div>
            ) : (
              <>
                {/* Header */}
                <div className="p-4 border-b border-gray-100 flex items-center gap-3">
                  <button onClick={() => setActiveConv(null)} className="md:hidden text-gray-500">← Back</button>
                  {(() => {
                    const conv = conversations.find((c) => String(c.id) === String(activeConv));
                    if (!conv) return null;
                    const isOnline = onlineUsers.has(String(conv.other_user_id));
                    return (
                      <>
                        <div className="relative">
                          <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center text-primary-700 font-semibold text-xs">
                            {conv.other_first_name?.[0]}{conv.other_last_name?.[0]}
                          </div>
                          <span className={`absolute bottom-0 right-0 w-2 h-2 rounded-full border border-white ${isOnline ? 'bg-green-400' : 'bg-gray-300'}`} />
                        </div>
                        <div>
                          <span className="font-semibold text-sm">{conv.other_first_name} {conv.other_last_name}</span>
                          <p className="text-xs text-gray-500">{otherTyping ? <span className="text-primary-600">typing…</span> : isOnline ? <span className="text-green-500">Online</span> : 'Offline'}</p>
                        </div>
                      </>
                    );
                  })()}
                </div>

                {/* Messages */}
                <div className="flex-1 overflow-y-auto p-4 space-y-3">
                  {messages.map((msg, i) => {
                    const isMine = String(msg.sender_id) === String(user.id);
                    return (
                      <div key={i} className={`flex ${isMine ? 'justify-end' : 'justify-start'}`}>
                        <div className={`max-w-xs lg:max-w-md px-4 py-2 rounded-2xl text-sm ${isMine ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-800'}`}>
                          <p>{msg.message || msg.content}</p>
                          <p className={`text-xs mt-1 ${isMine ? 'text-primary-200' : 'text-gray-400'}`}>
                            {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                  {otherTyping && (
                    <div className="flex justify-start">
                      <div className="bg-gray-100 text-gray-500 rounded-2xl px-4 py-2 text-sm flex items-center gap-1">
                        <span className="inline-block w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                        <span className="inline-block w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                        <span className="inline-block w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                      </div>
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </div>

                {/* Input */}
                <form onSubmit={handleSend} className="p-4 border-t border-gray-100 flex gap-2">
                  <input type="text" value={newMsg}
                    onChange={(e) => { setNewMsg(e.target.value); handleTyping(); }}
                    placeholder="Type a message..."
                    className="flex-1 border border-gray-300 rounded-full px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary-500" />
                  <button type="submit"
                    className="bg-primary-600 text-white px-6 py-2.5 rounded-full text-sm font-medium hover:bg-primary-700 disabled:opacity-50"
                    disabled={!newMsg.trim()}>Send</button>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MessagesPage;
